import React from "react";
import PropTypes from "prop-types";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

import Avatar from "@shared/v2/Avatar";
import Button from "@shared/v2/Button";
import IconButton from "@shared/v2/IconButton";
import Toggle from "@shared/v2/Toggle";
import { Popover } from "@shared/v2/Popover";
import {
  EllipsisSolidV6,
  GripDotsVerticalSolidV6,
  PencilSolidV6,
  StarRegularV6,
  StarSolidV6,
} from "@shared/v2/Icomoon";

const AgentLenderCard = ({ agent, className, popoverOptions, lender, onEdit, onStar, starDisabled }) => {
  const fullName = `${agent?.firstName || lender?.firstName} ${agent?.lastName || lender?.lastName}`.trim();
  return (
    <div
      className={`tw-flex-1 tw-basis-[230px] tw-max-w-[230px] tw-border-[1px] tw-border-solid tw-border-gray-10 tw-rounded-[4px] tw-p-[16px] tw-flex tw-flex-col tw-gap-[16px] ${className}`}
    >
      <div className="tw-flex tw-justify-between tw-items-start">
        {agent ? (
          <IconButton
            onClick={onStar}
            disabled={starDisabled}
            size="small"
            schema={agent.isFeatured ? "starred-yellow" : "tertiary"}
          >
            {agent.isFeatured ? <StarSolidV6 /> : <StarRegularV6 className="tw-text-gray-40" />}
          </IconButton>
        ) : (
          <span />
        )}
        <Avatar
          alt="Test Test"
          size="large"
          className={agent && !agent.showOnWebsiteRoster ? "tw-opacity-40" : ""}
        />
        {agent ? <GripDotsVerticalSolidV6 size="l" className="tw-ml-[8px]" /> : <span />}
      </div>
      <div
        className={`tw-flex tw-flex-col tw-items-center ${agent && !agent.showOnWebsiteRoster ? "tw-opacity-40" : ""}`}
      >
        <div className="tw-text-14d tw-font-bold">{fullName}</div>
        <div className="tw-text-14d tw-font-normal">{agent?.title || lender?.title}</div>
      </div>
      <div className="tw-min-h-[42px] tw-self-center tw-flex tw-flex-col tw-gap-[10px]">
        {agent && (
          <>
            <Toggle
              size="xsmall"
              labelClassName="tw-m-0 tw-font-semibold"
              text="Display on roster"
              checked={agent.showOnWebsiteRoster}
            />
            <Toggle
              size="xsmall"
              labelClassName="tw-m-0 tw-font-semibold"
              text="Profile page"
              checked={agent.showAgentProfilePage}
            />
          </>
        )}
      </div>
      <div className="tw-flex tw-gap-[8px]">
        <Popover placement="bottom-end">
          <Popover.Trigger>
            <IconButton size="small" schema="tertiary">
              <EllipsisSolidV6 />
            </IconButton>
          </Popover.Trigger>
          <Popover.Content>{popoverOptions}</Popover.Content>
        </Popover>
        <Button
          schema="secondary"
          className="tw-flex-1 tw-flex tw-gap-[4px] tw-justify-center tw-items-center"
          onClick={onEdit}
        >
          Edit <PencilSolidV6 size="s" />
        </Button>
      </div>
    </div>
  );
};

AgentLenderCard.propTypes = {
  agent: PropTypes.shape().isRequired,
  className: PropTypes.string,
  lender: PropTypes.shape().isRequired,
  onEdit: PropTypes.func.isRequired,
  popoverOptions: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  onStar: PropTypes.func,
  starDisabled: PropTypes.bool,
};

AgentLenderCard.defaultProps = {
  className: "",
  onStar: () => {},
  starDisabled: false,
};

const SortableAgentLenderCard = ({ id, agent, ...props }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.3 : 1,
    cursor: "grab",
    zIndex: isDragging ? 50 : "auto",
    flexBasis: "230px",
    maxWidth: "230px",
  };
  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
      <AgentLenderCard
        agent={agent}
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...props}
      />
    </div>
  );
};

SortableAgentLenderCard.displayName = "SortableAgentLenderCard";
SortableAgentLenderCard.propTypes = AgentLenderCard.propTypes;

export default AgentLenderCard;
export { SortableAgentLenderCard };
